import React from 'react';
import { ConversationScope } from 'Pages/AI/types';
import {
  AiInferenceStreamTextSearch,
  useAiInferenceStreamTextSearchLazyQuery,
} from '../graphql/aiInferenceStreamTextSearch.generated';
import { useGetGeneralSettings } from '../../../graphql/queries/getGeneralSettings.generated';

export const SEARCH_PAGE_SIZE = 10;

type SearchResultEdge = AiInferenceStreamTextSearch['aiInferenceStreamTextSearch']['edges'][0];
export type SearchResultNode = SearchResultEdge['node'];

interface UseKendraSearchOptions {
  isConversation: boolean;
  alias?: string;
  scope?: ConversationScope;
}

interface SearchOptions {
  cursor?: string;
  searchMineOverride?: boolean;
  searchAllOverride?: boolean;
}

export const getSearchLabel = (
  searchAll: boolean,
  searchMine: boolean,
  alias: string | undefined,
  isConversation: boolean,
): string => {
  const noun = isConversation ? 'conversations' : 'responses';
  if (searchAll && searchMine) {
    return `Search all ${noun} that are mine`;
  }
  if (searchAll) {
    return `Search all ${noun}`;
  }
  if (searchMine) {
    return `Search these ${noun} that are mine`;
  }
  if (isConversation) {
    return alias ? 'Search these conversations' : 'Search all conversations';
  }
  return `Search these ${noun}`;
};

export const useKendraSearch = ({ isConversation, alias, scope }: UseKendraSearchOptions) => {
  const { data: generalSettingsData } = useGetGeneralSettings();
  const isKendraEnabled = generalSettingsData?.generalSettings?.pantherAIKendra?.enabled ?? false;

  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchMine, setSearchMine] = React.useState(false);
  const [searchAll, setSearchAll] = React.useState(false);
  const [showSearchResults, setShowSearchResults] = React.useState(false);

  const [
    searchTextSearch,
    { data: searchResults, loading: searchLoading, error: searchError, client },
  ] = useAiInferenceStreamTextSearchLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const allSearchResults = searchResults?.aiInferenceStreamTextSearch?.edges ?? [];

  const hasSearched = showSearchResults && searchResults?.aiInferenceStreamTextSearch !== undefined;

  const handleSearch = React.useCallback(
    (query: string, options?: SearchOptions) => {
      if (query.trim()) {
        if (!options?.cursor && client) {
          client.cache.evict({ fieldName: 'aiInferenceStreamTextSearch' });
          client.cache.gc();
        }
        setShowSearchResults(true);
        const effectiveSearchAll = options?.searchAllOverride ?? searchAll;
        // When on the conversational AI page, scope is always 'owned' so always search mine.
        // Otherwise fall back to the manual searchMine toggle (AI side panel).
        const effectiveSearchMine =
          scope !== undefined || (options?.searchMineOverride ?? searchMine);
        searchTextSearch({
          variables: {
            input: {
              query: query.trim(),
              alias: effectiveSearchAll ? undefined : alias,
              isConversation,
              searchMine: effectiveSearchMine || undefined,
              cursor: options?.cursor,
              pageSize: SEARCH_PAGE_SIZE,
            },
          },
          ...(options?.cursor ? { fetchPolicy: 'network-only' as const } : {}),
        });
      }
    },
    [searchTextSearch, searchMine, searchAll, alias, isConversation, client, scope],
  );

  const handleInitialSearch = React.useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSearchAll(true);
      handleSearch(query, { searchAllOverride: true });
    },
    [handleSearch],
  );

  const handleClearResults = React.useCallback(() => {
    setShowSearchResults(false);
    if (client) {
      client.cache.evict({ fieldName: 'aiInferenceStreamTextSearch' });
      client.cache.gc();
    }
  }, [client]);

  const handleLoadMore = React.useCallback(() => {
    const pageInfo = searchResults?.aiInferenceStreamTextSearch?.pageInfo;
    if (pageInfo?.hasNextPage && pageInfo?.endCursor && !searchLoading) {
      handleSearch(searchQuery, { cursor: pageInfo.endCursor });
    }
  }, [searchResults, searchQuery, searchLoading, handleSearch]);

  return {
    searchQuery,
    setSearchQuery,
    searchAll,
    setSearchAll,
    searchMine,
    setSearchMine,
    showSearchResults,
    setShowSearchResults,
    hasSearched,
    allSearchResults,
    handleSearch,
    handleInitialSearch,
    handleClearResults,
    handleLoadMore,
    searchLoading,
    searchError,
    searchResults,
    searchTextSearch,
    isKendraEnabled,
    totalCount: searchResults?.aiInferenceStreamTextSearch?.totalCount,
    searchHasNextPage: searchResults?.aiInferenceStreamTextSearch?.pageInfo?.hasNextPage,
  };
};
